import Image from "next/image";
import { Container } from "@/components/ui/Container";
import { cn } from "@/components/ui/cn";

/**
 * Full-bleed hero banner for public marketing detail pages (course / event /
 * podcast / ott). Restores legacy top-header parity: a viewport-width cover
 * image with the title + badges + meta overlaid at the bottom on a legibility
 * scrim. When the module has no cover, a branded teal→ink gradient shows
 * through instead of a broken image, so the header still reads as intentional.
 *
 * Layout: the image is full-bleed; `children` (badges/title/meta) are aligned
 * to the page Container and pinned to the bottom. The banner has no height of
 * its own — the Container's minimum (with a cover) or its padding plus content
 * (without) decides it, see the class notes below. The breadcrumb renders on a
 * light strip BELOW this banner (mirrors the listing pages).
 *
 * `overlayHeader`: set on the guest/public surface, where the marketing header
 * is `fixed` and transparent over this banner — adds top padding so the banner
 * content clears the header (matches ListingHero). Leave false on the in-app
 * (AppShell) surface, whose header is solid and in-flow.
 *
 * `containerSize`: the Container the overlaid text aligns to — the default page
 * container for the module details, `reading` (or `narrow`) for a page whose
 * body is a reading column, so the title's left edge is the text's.
 */
export function DetailHeroBanner({
  imageUrl,
  imageAlt,
  overlayHeader = false,
  containerSize = "default",
  reserveCover = false,
  children,
}: {
  imageUrl: string | null;
  /** From moduleBannerAlt — the cover is the page's picture of the module, never chrome. */
  imageAlt: string;
  overlayHeader?: boolean;
  containerSize?: "default" | "narrow" | "reading";
  /** Size the band as if a cover were coming, without one — the loading
   *  skeletons (components/modules/DetailSkeletons.tsx) render this very
   *  component, so the height a page reserves is the height it gets. */
  reserveCover?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      data-header-overlay={overlayHeader ? "" : undefined}
      className="relative isolate w-full overflow-hidden text-white"
    >
      {/* Branded base — visible when a module has no cover image. */}
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-teal to-foreground" />
      {imageUrl ? (
        <Image src={imageUrl} alt={imageAlt} fill priority sizes="100vw" className="object-cover" />
      ) : null}
      {/* Legibility scrim so white text reads on any image. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20"
      />
      <Container
        size={containerSize}
        className={cn(
          "relative flex flex-col justify-end pb-9 md:pb-12",
          // The height follows the image (D35, step 2). With a cover: the band
          // size the video hero uses — 260 px on phones, 340 px from tablets —
          // and never more than 40 % of the viewport, so a 1366 × 768 laptop
          // gets ~300 px where a fixed 420 put the article's first line 61 %
          // down the screen. Without one there is no minimum: the band is
          // padding plus content, and a text-only article does not open on a
          // slab of gradient.
          imageUrl || reserveCover ? "min-h-[260px] md:min-h-[clamp(260px,40vh,340px)]" : null,
          // Clear the fixed, transparent marketing header (guest/public only).
          // In the app shell nothing overlays the banner, so the top padding is
          // breathing room, not clearance — 96 px of it was empty.
          overlayHeader ? "pt-28 md:pt-40" : "pt-10",
        )}
      >
        {children}
      </Container>
    </section>
  );
}
